import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Typography, ConfigProvider, theme } from 'antd';
import { FilterFilled, LayoutOutlined } from '@ant-design/icons';
import TemplatePageHeader from './TemplatePageHeader';
import TemplatePageSearch from './TemplatePageSearch';
import TemplateManagementTable from './TemplateManagementTable';
import GalleryView from './GalleryView';
import KanbanView from './KanbanView';
import ViewToggle from './ViewToggle';
import AdvancedFilters from './AdvancedFilters';
import BulkOperations from './BulkOperations';
import OverrideManager from './OverrideManager';
import VersionHistory from './VersionHistory';
import ImportExport from './ImportExport';
import ABTestManager from './ABTestManager';

const { Title } = Typography;

/**
 * TemplateManager - Main container for template management
 * Orchestrates all template management components
 */
const TemplateManager = () => {
  const navigate = useNavigate();

  // State management
  const [filters, setFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // View mode state (persisted to localStorage)
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('templateViewMode') || 'table';
  });

  // Selection state for bulk operations
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // Advanced filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Modal states
  const [overrideVisible, setOverrideVisible] = useState(false);
  const [versionVisible, setVersionVisible] = useState(false);
  const [importExportVisible, setImportExportVisible] = useState(false);
  const [abTestVisible, setAbTestVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Handlers
  const handleCreateNew = () => {
    navigate('/template-management/create');
  };

  const handleEdit = (template) => {
    navigate(`/template-management/edit/${template.templateId}`);
  };

  const handlePreview = (template) => {
    navigate(`/template-management/preview/${template.templateId}`);
  };

  const handleViewStats = (template) => {
    navigate(`/template-analytics?templateId=${template.templateId}`);
  };

  const handleViewHistory = (template) => {
    setSelectedTemplate(template);
    setVersionVisible(true);
  };

  const handleViewOverrides = (template) => {
    setSelectedTemplate(template);
    setOverrideVisible(true);
  };

  const handleViewAnalytics = () => {
    navigate('/template-analytics');
  };

  const handleImport = () => {
    setImportExportVisible(true);
  };

  const handleExport = () => {
    setImportExportVisible(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (searchFilters) => {
    setFilters(searchFilters);
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setSelectedTemplates([]);
  };

  const handleSelectionChange = (selected) => {
    setSelectedTemplates(selected);
  };

  const handleClearSelection = () => {
    setSelectedTemplates([]);
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(prev => !prev);
  };

  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    localStorage.setItem('templateViewMode', newMode);
  };

  // Render the active view based on viewMode
  const renderActiveView = () => {
    const commonProps = {
      key: refreshKey,
      filters,
      onEdit: handleEdit,
      onPreview: handlePreview,
      onViewStats: handleViewStats,
      onViewHistory: handleViewHistory,
      onViewOverrides: handleViewOverrides,
      onRefresh: handleRefresh,
    };

    switch (viewMode) {
      case 'gallery':
        return <GalleryView {...commonProps} />;
      case 'kanban':
        return <KanbanView {...commonProps} />;
      case 'table':
      default:
        return (
          <TemplateManagementTable
            {...commonProps}
            selectedTemplates={selectedTemplates}
            onSelectionChange={handleSelectionChange}
          />
        );
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#0a192f',
          borderRadius: 8,
        },
      }}
    >
      <Container>
        <TemplatePageHeader
          onCreateNew={handleCreateNew}
          onImport={handleImport}
          onExport={handleExport}
          onViewAnalytics={handleViewAnalytics}
        />

        <ControlCenter>
          <SearchWrapper>
             <TemplatePageSearch
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
            />
          </SearchWrapper>
          
          <ToolbarRow>
            <SectionTitle>
              <LayoutOutlined style={{ marginRight: 10, color: 'var(--aura-blue)' }} />
              <span>Templates List</span>
            </SectionTitle>
            
            <ToolbarActions>
              <ViewToggle value={viewMode} onChange={handleViewModeChange} />
              <AdvancedFilterButton 
                onClick={toggleAdvancedFilters}
                $active={showAdvancedFilters}
                icon={<FilterFilled />}
              >
                {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              </AdvancedFilterButton>
            </ToolbarActions>
          </ToolbarRow>
        </ControlCenter>

        {showAdvancedFilters && (
          <FilterSlideDown>
            <AdvancedFilters
              onFilterChange={handleFilterChange}
              initialFilters={filters}
            />
          </FilterSlideDown>
        )}

        {viewMode === 'table' && selectedTemplates.length > 0 && (
          <BulkOpsWrapper>
            <BulkOperations
              selectedTemplates={selectedTemplates}
              onClearSelection={handleClearSelection}
              onRefresh={handleRefresh}
            />
          </BulkOpsWrapper>
        )}

        <ViewSection>
          {renderActiveView()}
        </ViewSection>

        <OverrideManager
          visible={overrideVisible}
          onClose={() => setOverrideVisible(false)}
          template={selectedTemplate}
        />

        <VersionHistory
          visible={versionVisible}
          onClose={() => setVersionVisible(false)}
          template={selectedTemplate}
          onRollback={handleRefresh}
        />

        <ImportExport
          visible={importExportVisible}
          onClose={() => setImportExportVisible(false)}
          templates={templates}
          onImportSuccess={handleRefresh}
        />

        <ABTestManager
          visible={abTestVisible}
          onClose={() => setAbTestVisible(false)}
          templates={templates}
        />
      </Container>
    </ConfigProvider>
  );
};

const Container = styled.div`
  padding: 24px;
`;

const ControlCenter = styled.div`
  margin-bottom: 32px;
`;

const SearchWrapper = styled.div`
  margin-bottom: 24px;
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 2px;
  color: var(--aura-navy);
  opacity: 0.8;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AdvancedFilterButton = styled(Button)`
  border-radius: 10px !important;
  height: 40px !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
  border-color: ${props => props.$active ? 'var(--aura-blue)' : '#d9d9d9'} !important;
  color: ${props => props.$active ? 'var(--aura-blue)' : 'inherit'} !important;
  background: ${props => props.$active ? 'rgba(76, 201, 240, 0.05)' : 'white'} !important;
`;

const FilterSlideDown = styled.div`
  animation: slideDown 0.3s ease-out;
  margin-bottom: 24px;

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const BulkOpsWrapper = styled.div`
  margin-bottom: 24px;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ViewSection = styled.div`
  animation: fadeIn 0.5s ease-out;
`;

export default TemplateManager;

